import dayjs from 'dayjs'
import React from 'react'
import { Link } from 'react-router-dom'

import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { useAuth } from '@fourthwall/auth'
import type { BannerProps } from '@fourthwall/components'
import { getPrice } from '@fourthwall/utils/dist/common'
import { pluralize } from '@fourthwall/utils/dist/string'
import { usePastDueSubscriptionErrorBanner } from '@modules/Dashboard/modules/Subscriptions'
import { useCreatorIntegrationsQuery } from '@modules/Dashboard/pages/Apps'
import { useTiktokShopConfigurationQuery } from '@modules/Dashboard/pages/Apps/pages/AppsTiktokShop'
import { useCreatorIntegrationsV2Query } from '@queries'

import { routing } from '../../../utils/routing'
import { useCurrentShopQuery } from './useCurrentShopQuery'
import { useMarkBlockedTransactionsAsSeenMutation } from './useMarkBlockedTransactionsAsSeenMutation'
import { useSendVerifyEmailMutation } from './useSendVerifyEmailMutation'

type UseBanners = () => { bannersProps: BannerProps[] }

export const useBanners: UseBanners = () => {
  const { keycloak } = useAuth()

  const { sendVerifyEmailMutation } = useSendVerifyEmailMutation()
  const { hasPermission } = useDashboardPermissions()
  const pastDueSubscriptionErrorBanner = usePastDueSubscriptionErrorBanner({ withLink: true })
  const { creatorIntegrationsQuery } = useCreatorIntegrationsQuery(undefined, {
    enabled: hasPermission('apps'),
  })
  const { creatorIntegrationsV2Query } = useCreatorIntegrationsV2Query(undefined, {
    enabled: hasPermission('apps'),
  })
  const { currentShopQuery, invalidateCurrentShopQuery, setCurrentShopData } = useCurrentShopQuery(
    undefined,
    {
      staleTime: Infinity,
    },
  )
  const { tiktokShopConfigurationQuery } = useTiktokShopConfigurationQuery([], {
    enabled: creatorIntegrationsQuery.data?.tiktokPartner?.status === 'CONNECTED',
  })
  const { markBlockedTransactionsAsSeenMutation } = useMarkBlockedTransactionsAsSeenMutation({
    onMutate: () => {
      setCurrentShopData((currentData) => {
        if (!currentData) {
          return currentData
        }

        return {
          ...currentData,
          blockedTransactionsStatus: { type: 'ALL_SEEN' },
        }
      })

      return undefined
    },
  })
  const shipStationApp = creatorIntegrationsV2Query.data?.apps.find(
    ({ app }) => app === 'SHIPSTATION',
  )

  const resendEmail = () => {
    sendVerifyEmailMutation.mutate()
  }

  const markBlockedTransactionsAsSeen = () => {
    markBlockedTransactionsAsSeenMutation.mutate([], {
      onSuccess: () => {
        invalidateCurrentShopQuery()
      },
    })
  }

  const shopCreatedMoreThan24HoursAgo = currentShopQuery.data?.createdAt
    ? dayjs().diff(dayjs(currentShopQuery.data.createdAt), 'hours') >= 24
    : true // If we can't determine creation date, show the banner to be safe

  const banners: { when: boolean; bannerProps: BannerProps }[] = [
    {
      // @ts-ignore
      when: keycloak?.tokenParsed?.email_verified === false && shopCreatedMoreThan24HoursAgo,
      bannerProps: {
        children: (
          <>
            Please verify your email address to activate your account.{' '}
            <button onClick={resendEmail}>Resend email</button>
          </>
        ),
        appearance: 'warning',
      },
    },
    {
      when: !!creatorIntegrationsQuery.data?.twitch?.missingScopes?.length,
      bannerProps: {
        children: (
          <>
            Twitch requires a permissions update in order for Twitch apps to work.{' '}
            <Link to={routing.apps.twitch}>Update permissions</Link>
          </>
        ),
        appearance: 'warning',
      },
    },
    {
      when:
        tiktokShopConfigurationQuery.data?.type === 'CONFIGURED' &&
        !!tiktokShopConfigurationQuery.data.authorizationExpiresAt,
      bannerProps: {
        children: (
          <>
            TikTok Shop connection expires on{' '}
            {dayjs(
              tiktokShopConfigurationQuery.data?.type === 'CONFIGURED'
                ? tiktokShopConfigurationQuery.data.authorizationExpiresAt
                : undefined,
            ).format('MMM D, YYYY')}
            . <Link to={routing.apps.tiktokShop}>Update connection</Link>
          </>
        ),
        appearance: 'warning',
      },
    },
    {
      when:
        currentShopQuery.data?.blockedTransactionsStatus?.type === 'PAYMENT_METHOD_LIMIT_EXCEEDED',
      bannerProps: {
        children: (() => {
          if (!currentShopQuery.isSuccess) return

          const { blockedTransactionsStatus } = currentShopQuery.data

          if (blockedTransactionsStatus.type !== 'PAYMENT_METHOD_LIMIT_EXCEEDED') return

          const { numberOfBlockedTransactions: count, limit } = blockedTransactionsStatus

          return (
            <>
              We blocked {count} {pluralize(count, 'transaction')} that would have taken you past
              your monthly charge limit
              {limit ? ` (${getPrice(limit.value, { currency: limit.currency })})` : ''}
              .&nbsp;&nbsp;
              <Link to={routing.settings.billing.self}>Change limit</Link> &nbsp;&nbsp;
              <Link to={routing.blockedTransactions}>View blocked transactions</Link>
            </>
          )
        })(),
        appearance: 'danger',
        onClose: () => {
          markBlockedTransactionsAsSeen()
        },
      },
    },
    {
      when: currentShopQuery.data?.blockedTransactionsStatus?.type === 'NO_PAYMENT_METHOD',
      bannerProps: {
        children: (() => {
          if (!currentShopQuery.isSuccess) return

          const { blockedTransactionsStatus } = currentShopQuery.data

          if (blockedTransactionsStatus.type !== 'NO_PAYMENT_METHOD') return

          const { numberOfBlockedTransactions: count } = blockedTransactionsStatus

          return (
            <>
              We blocked {count} {pluralize(count, 'transaction')} that could not be covered by your
              account balance.&nbsp;&nbsp;
              <Link to={routing.settings.billing.self}>Add payment method</Link> &nbsp;&nbsp;
              <Link to={routing.blockedTransactions}>View blocked transactions</Link>
            </>
          )
        })(),
        appearance: 'danger',
        onClose: () => {
          markBlockedTransactionsAsSeen()
        },
      },
    },
    {
      when: currentShopQuery.data?.blockedTransactionsStatus?.type === 'PAYMENT_METHOD_NOT_VALID',
      bannerProps: {
        children: (
          <>
            We were unable to process your payment. Transactions that incur losses will be blocked.
            &nbsp;&nbsp;
            <Link to={routing.settings.billing.self}>Update payment method</Link> &nbsp;&nbsp;
            <Link to={routing.blockedTransactions}>View blocked transactions</Link>
          </>
        ),
        appearance: 'danger',
        onClose: () => {
          markBlockedTransactionsAsSeen()
        },
      },
    },
    {
      when: shipStationApp?.status === 'BROKEN',
      bannerProps: {
        children: (
          <>
            Your ShipStation integration is inactive. Orders will not be sent. &nbsp;&nbsp;
            <Link to={routing.apps.shipStation}>Reconnect now</Link>
          </>
        ),
        appearance: 'danger',
      },
    },
    {
      when: pastDueSubscriptionErrorBanner.isActive,
      bannerProps: {
        children: pastDueSubscriptionErrorBanner.content,
        appearance: 'danger',
        onClose: () => {
          markBlockedTransactionsAsSeen()
        },
      },
    },
  ]

  return {
    bannersProps: banners
      .filter(({ when }) => when)
      .map<BannerProps>(({ bannerProps }) => ({ ...bannerProps, justify: 'center' })),
  }
}
